import React, { useContext, useEffect, useRef, useState } from "react";
import { Alert, Button, Col, Container, Form, Row } from "react-bootstrap";
import { VscNewFile } from "react-icons/vsc";
import { BsArrowLeft, BsSearch } from "react-icons/bs";
import { useNavigate } from "react-router-dom";

import Cabecalho from "../../../components/Cabecalho/Cabecalho";
import BoletimInformacoes from "./components/BoletimInformacoes";
import LoadSpinner from "../../../components/LoadSpinner/LoadSpinner";
import { BoletimContext } from "../../../context/BoletimContext";
import api from "../../../Utils/api";

import "../styles/dashboard-modern.css";
import "./styles/dashboard-boletim.css";

const DashboardBoletim = () => {
  const navigate = useNavigate();
  const { boletim, setBoletim, discardCurrentDraft, syncStatus } = useContext(BoletimContext);
  const [filters, setFilters] = useState({ date: "", number: "", involved: "", keyword: "", nature: "", location: "", city: "" });
  const [reports, setReports] = useState([]);
  const [searched, setSearched] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState("");
  const [pagination, setPagination] = useState(null);
  const [appliedFilters, setAppliedFilters] = useState(null);
  const [pdfMode, setPdfMode] = useState('summary');
  const [exporting, setExporting] = useState(false);
  const [success, setSuccess] = useState('');
  const requestRef = useRef({ version: 0, controller: null });
  const exportRef = useRef(null);
  const versionRef = useRef(0);

  useEffect(() => () => {
    requestRef.current.version++;
    requestRef.current.controller?.abort();
    exportRef.current?.abort();
  }, []);

  const resetResults = () => {
    requestRef.current.version++;
    requestRef.current.controller?.abort();
    exportRef.current?.abort();
    versionRef.current++;
    setReports([]); setPagination(null); setSearched(false); setIsLoading(false);
    setAppliedFilters(null); setError(''); setSuccess(''); setExporting(false);
  };


  const updateFilter = (field) => (event) => {
    resetResults();
    setFilters((current) => ({ ...current, [field]: event.target.value }));
  };

  const loadPage = async (params, page = 1) => {
    requestRef.current.controller?.abort();
    const controller = new AbortController();
    const version = ++requestRef.current.version;
    requestRef.current.controller = controller;
    setError(''); setSuccess(''); setReports([]); setIsLoading(true); setSearched(true);
    try {
      const { data } = await api.get('/incidentReport/search', { params: { ...params, page }, signal: controller.signal });
      if (version !== requestRef.current.version) return;
      setReports(data.data); setPagination(data.pagination); setAppliedFilters(params);
    } catch (requestError) {
      if (version !== requestRef.current.version || controller.signal.aborted) return;
      setReports([]); setPagination(null);
      setError(requestError.response?.data?.message || 'Falha de conexão na pesquisa. Tente novamente.');
    } finally {
      if (version === requestRef.current.version) setIsLoading(false);
    }
  };

  const searchReports = async (event) => {
    event.preventDefault();
    const params = Object.fromEntries(
      Object.entries(filters).filter(([, value]) => value.trim())
    );
    if (!Object.keys(params).length) {
      setError("Preencha ao menos um campo para pesquisar.");
      setReports([]);
      setSearched(false);
      return;
    }

    exportRef.current?.abort();
    versionRef.current++;
    setExporting(false);
    setPagination(null);
    await loadPage(params, 1);
  };

  const exportPdf = async () => {
    if (!appliedFilters || exporting) return;
    const controller = new AbortController();
    exportRef.current = controller;
    const version = versionRef.current;
    setExporting(true); setError(''); setSuccess('');
    try {
      const response = await api.get('/incidentReport/search/export/pdf', {
        params: { ...appliedFilters, mode: pdfMode }, responseType: 'blob', timeout: 180000, signal: controller.signal
      });
      if (version !== versionRef.current || controller.signal.aborted) return;
      const url = URL.createObjectURL(response.data);
      const link = document.createElement('a');
      link.href = url; link.download = `boletins-${pdfMode === 'detailed' ? 'detalhado' : 'resumido'}.pdf`;
      document.body.appendChild(link); link.click(); link.remove();
      setTimeout(() => URL.revokeObjectURL(url), 1000);
      setSuccess('PDF gerado com todos os resultados filtrados. Download iniciado.');
    } catch (requestError) {
      if (version !== versionRef.current || controller.signal.aborted) return;
      let message = requestError.response?.data?.message;
      if (requestError.response?.data instanceof Blob) {
        try { message = JSON.parse(await requestError.response.data.text()).message; } catch { /* Connection or invalid response. */ }
      }
      if (version === versionRef.current && !controller.signal.aborted) setError(message || 'Falha no download do PDF. Verifique sua conexão e tente novamente.');
    } finally {
      if (version === versionRef.current) setExporting(false);
    }
  };

  const handleClickNovoBoletim = async () => {
    const newBoletim = {
      municipio: localStorage.getItem("x-user-municipio") || "Balsas",
      envolvidos: [],
      materiaisApreendidos: [],
      efetivo: [],
      historicohtml: "",
    };
    const hasDraft = syncStatus.state !== "synced" && (
      boletim.numero || boletim.data || boletim.natureza || boletim.historicohtml ||
      boletim.envolvidos?.length || boletim.materiaisApreendidos?.length || boletim.efetivo?.length
    );
    if (hasDraft && !window.confirm("Há um boletim em andamento. Deseja descartá-lo e iniciar outro?")) {
      navigate("/boletim/header");
      return;
    }
    if (hasDraft) await discardCurrentDraft();
    setBoletim(newBoletim);
    navigate("/boletim/header");
  };

  return (
    <>
      <Cabecalho />
      <main className="dashboard-modern mt-3 mb-5">
        <Container>
          <div className="search-page-heading">
            <div>
              <p className="dashboard-eyebrow">Consulta de registros</p>
              <h2>Pesquisar boletins</h2>
              <p className="dashboard-intro">
                Pesquise os boletins oficiais do sistema.
              </p>
            </div>
          </div>

          <Form className="report-search-form" onSubmit={searchReports}>
            <Row className="g-3">
              <Col xs={12} sm={6} lg={3}>
                <Form.Group controlId="search-date">
                  <Form.Label>Data do fato</Form.Label>
                  <Form.Control type="date" value={filters.date} onChange={updateFilter("date")} />
                </Form.Group>
              </Col>
              <Col xs={12} sm={6} lg={3}>
                <Form.Group controlId="search-number">
                  <Form.Label>Número do boletim</Form.Label>
                  <Form.Control inputMode="numeric" value={filters.number} onChange={updateFilter("number")} placeholder="Número" />
                </Form.Group>
              </Col>
              <Col xs={12} sm={6} lg={3}>
                <Form.Group controlId="search-involved">
                  <Form.Label>Envolvido</Form.Label>
                  <Form.Control value={filters.involved} onChange={updateFilter("involved")} placeholder="Nome do envolvido" />
                </Form.Group>
              </Col>
              <Col xs={12} sm={6} lg={3}>
                <Form.Group controlId="search-keyword">
                  <Form.Label>Palavra-chave</Form.Label>
                  <Form.Control value={filters.keyword} onChange={updateFilter("keyword")} placeholder="Natureza, local ou relato" />
                </Form.Group>
              </Col>
              {[["nature", "Natureza"], ["location", "Local da ocorrência"], ["city", "Município"]].map(([field, label]) => (
                <Col xs={12} sm={6} lg={4} key={field}>
                  <Form.Group controlId={`search-${field}`}>
                    <Form.Label>{label}</Form.Label>
                    <Form.Control value={filters[field]} onChange={updateFilter(field)} maxLength={120} />
                  </Form.Group>
                </Col>
              ))}
            </Row>
            {error && <Alert variant="danger" className="mt-3 mb-0">{error}</Alert>}
            <div className="d-flex flex-column flex-sm-row gap-2 mt-3">
              <Button type="submit" className="search-submit" disabled={isLoading}>
                <BsSearch className="me-2" /> Pesquisar
              </Button>
              <Button type="button" variant="outline-secondary" onClick={() => {
                resetResults(); setFilters({ date: '', number: '', involved: '', keyword: '', nature: '', location: '', city: '' });
              }}>Limpar filtros</Button>
              <Button type="button" variant="outline-secondary" onClick={() => navigate("/dashboard")}>
                <BsArrowLeft className="me-2" /> Voltar
              </Button>
            </div>
          </Form>

          {searched && pagination && !isLoading && <section className="search-result-toolbar mt-3" aria-label="Resultados e exportação">
            <p role="status" className="mb-0"><strong>{pagination.total} boletins encontrados</strong><br />20 registros por página.</p>
            <Form.Group controlId="pdf-mode">
              <Form.Label>Modalidade de PDF</Form.Label>
              <Form.Select value={pdfMode} onChange={event => setPdfMode(event.target.value)} disabled={exporting}>
                <option value="summary">PDF resumido</option>
                <option value="detailed">PDF detalhado</option>
              </Form.Select>
            </Form.Group>
            <Button onClick={exportPdf} disabled={exporting || isLoading}>{exporting ? 'Gerando PDF...' : 'Gerar PDF dos resultados'}</Button>
            {exporting && <Button variant="outline-secondary" onClick={() => {
              exportRef.current?.abort(); versionRef.current++; setExporting(false); setSuccess('Exportação cancelada.');
            }}>Cancelar exportação</Button>}
          </section>}
          {success && <Alert variant="success" role="status" className="mt-3">{success}</Alert>}

          <LoadSpinner visible={isLoading} />
          {searched && !isLoading && !error && reports.length === 0 && (
            <Alert variant="info" className="mt-4">Nenhum boletim encontrado com os filtros informados.</Alert>
          )}
          {reports.map((report) => (
            <BoletimInformacoes key={report._id} boletim={report} setBoletim={setBoletim} />
          ))}
          {searched && pagination && !isLoading && <nav className="search-pagination mt-3" aria-label="Paginação de boletins">
            <p className="mb-0">Página {pagination.page} de {pagination.totalPages}</p>
            {pagination.totalPages > 1 && <>
              <Button variant="outline-primary" disabled={!pagination.hasPreviousPage || exporting} onClick={() => loadPage(appliedFilters, pagination.page - 1)}>Anterior</Button>
              <Form className="search-page-picker" onSubmit={event => {
                event.preventDefault();
                const page = Number(new FormData(event.currentTarget).get('page'));
                if (Number.isInteger(page) && page >= 1 && page <= pagination.totalPages) loadPage(appliedFilters, page);
              }}>
                <Form.Group controlId="search-page">
                  <Form.Label className="visually-hidden">Selecionar página</Form.Label>
                  <Form.Control name="page" type="number" min="1" max={pagination.totalPages} defaultValue={pagination.page} required disabled={exporting} />
                </Form.Group>
                <Button type="submit" variant="outline-primary" disabled={exporting}>Ir</Button>
              </Form>
              <Button variant="outline-primary" disabled={!pagination.hasNextPage || exporting} onClick={() => loadPage(appliedFilters, pagination.page + 1)}>Próxima</Button>
            </>}
          </nav>}

          <Row className="justify-content-center mt-4 mx-0">
            <Col xs={12} sm={7} md={5} className="d-grid">
              <Button variant="dark" onClick={handleClickNovoBoletim}>
                Registrar ocorrência <VscNewFile />
              </Button>
            </Col>
          </Row>
        </Container>
      </main>
    </>
  );
};

export default DashboardBoletim;
